import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BASE_URL from '../../services/baseUrl';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const PLATFORMS = ['facebook', 'instagram', 'twitter', 'youtube', 'tiktok', 'telegram', 'discord', 'whatsapp', 'snapchat', 'linkedin', 'other'];

const platformIcon = (platform) => {
    const icons = {
        facebook: '📘', instagram: '📸', twitter: '🐦', youtube: '▶️',
        tiktok: '🎵', telegram: '✈️', discord: '🎮', whatsapp: '💬',
        snapchat: '👻', linkedin: '💼', other: '🔗',
    };
    return icons[platform?.toLowerCase()] || '🔗';
};

const defaultForm = { platform: 'facebook', url: '', name: '', isActive: true, order: '' };

const SocialMediaManagement = () => {
    const token = localStorage.getItem('accessToken');
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    const loadLinks = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/admin/social`, { headers });
            setLinks(res.data.data || []);
        } catch {
            toast.error('Failed to load social links');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadLinks(); }, []);

    const openAdd = () => {
        setEditTarget(null);
        setForm(defaultForm);
        setShowModal(true);
    };

    const openEdit = (link) => {
        setEditTarget(link);
        setForm({
            platform: link.platform || 'other',
            url: link.url || '',
            name: link.name || '',
            isActive: link.isActive !== false,
            order: link.order ?? '',
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditTarget(null);
        setForm(defaultForm);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.url) return toast.error('URL is required');
        setSubmitting(true);
        try {
            const payload = { ...form, order: form.order !== '' ? Number(form.order) : undefined };
            if (editTarget) {
                await axios.put(`${BASE_URL}/admin/social/${editTarget._id}`, payload, { headers });
                toast.success('Social link updated!');
            } else {
                await axios.post(`${BASE_URL}/admin/social`, payload, { headers });
                toast.success('Social link created!');
            }
            closeModal();
            loadLinks();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save social link');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggle = async (link) => {
        try {
            await axios.patch(`${BASE_URL}/admin/social/${link._id}/toggle`, {}, { headers });
            toast.success(`${link.platform} ${link.isActive ? 'deactivated' : 'activated'}`);
            loadLinks();
        } catch {
            toast.error('Failed to toggle status');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.delete(`${BASE_URL}/admin/social/${deleteTarget._id}`, { headers });
            toast.success(`${deleteTarget.platform} deleted`);
            setDeleteTarget(null);
            loadLinks();
        } catch {
            toast.error('Failed to delete');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="fw-bold mb-0">Social Media Links</h2>
                    <p className="text-muted small mb-0">Manage social media icons shown on the platform</p>
                </div>
                <button className="btn btn-primary" onClick={openAdd}>+ Add Social Link</button>
            </div>

            {/* Stats */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Total</p>
                            <h3 className="fw-bold text-primary mb-0">{links.length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Active</p>
                            <h3 className="fw-bold text-success mb-0">{links.filter(l => l.isActive).length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Inactive</p>
                            <h3 className="fw-bold text-danger mb-0">{links.filter(l => !l.isActive).length}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary me-2" role="status"></div>
                            <span className="text-muted">Loading...</span>
                        </div>
                    ) : links.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="fs-1 mb-2">🔗</div>
                            <p className="text-muted">No social links yet. Add your first one!</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">#</th>
                                        <th>Platform</th>
                                        <th>Name</th>
                                        <th className="d-none d-md-table-cell">URL</th>
                                        <th>Order</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {links.map((link, i) => (
                                        <tr key={link._id}>
                                            <td className="ps-3 text-muted small">{i + 1}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ fontSize: '1.4rem' }}>{platformIcon(link.platform)}</span>
                                                    <span className="fw-semibold text-capitalize">{link.platform}</span>
                                                </div>
                                            </td>
                                            <td className="text-muted small">{link.name || '—'}</td>
                                            <td className="d-none d-md-table-cell">
                                                <a href={link.url} target="_blank" rel="noreferrer"
                                                    className="text-decoration-none small text-primary">
                                                    {link.url?.length > 40 ? link.url.slice(0, 40) + '…' : link.url}
                                                </a>
                                            </td>
                                            <td className="text-muted small">{link.order ?? '—'}</td>
                                            <td>
                                                <span className={`badge ${link.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                    {link.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(link)}>
                                                        Edit
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${link.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                        onClick={() => handleToggle(link)}
                                                    >
                                                        {link.isActive ? 'Disable' : 'Enable'}
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteTarget(link)}>
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">
                                        {editTarget ? `Edit — ${editTarget.platform}` : 'Add Social Link'}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={closeModal}></button>
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={handleSubmit} id="socialForm">
                                        <div className="row g-3">
                                            <div className="col-sm-6">
                                                <label className="form-label fw-semibold">Platform <span className="text-danger">*</span></label>
                                                <select className="form-select" name="platform" value={form.platform} onChange={handleChange}>
                                                    {PLATFORMS.map(p => (
                                                        <option key={p} value={p}>
                                                            {platformIcon(p)} {p.charAt(0).toUpperCase() + p.slice(1)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-sm-6">
                                                <label className="form-label fw-semibold">Display Name</label>
                                                <input type="text" className="form-control" name="name"
                                                    value={form.name} onChange={handleChange}
                                                    placeholder="e.g. Follow us on Facebook" />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold">URL <span className="text-danger">*</span></label>
                                                <input type="url" className="form-control" name="url"
                                                    value={form.url} onChange={handleChange}
                                                    placeholder="https://facebook.com/gorillaFishingRoom" required />
                                            </div>
                                            <div className="col-sm-6">
                                                <label className="form-label fw-semibold">Display Order</label>
                                                <input type="number" className="form-control" name="order"
                                                    value={form.order} onChange={handleChange}
                                                    placeholder="1, 2, 3..." min="1" />
                                            </div>
                                            <div className="col-sm-6 d-flex align-items-end">
                                                <div className="form-check form-switch mb-2">
                                                    <input className="form-check-input" type="checkbox" role="switch"
                                                        id="isActive" name="isActive"
                                                        checked={form.isActive} onChange={handleChange} />
                                                    <label className="form-check-label fw-semibold" htmlFor="isActive">
                                                        Active (visible on site)
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                                    <button type="submit" form="socialForm" className="btn btn-primary" disabled={submitting}>
                                        {submitting ? (
                                            <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Saving...</>
                                        ) : editTarget ? 'Save Changes' : 'Create Link'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}

            {/* Delete Confirm Modal */}
            {deleteTarget && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0 pb-0">
                                    <h5 className="modal-title fw-bold text-danger">Delete Social Link</h5>
                                    <button type="button" className="btn-close" onClick={() => setDeleteTarget(null)} disabled={deleting}></button>
                                </div>
                                <div className="modal-body pt-2">
                                    <p>Delete <strong>{deleteTarget.platform}</strong> link? This cannot be undone.</p>
                                </div>
                                <div className="modal-footer border-0 pt-0">
                                    <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
                                    <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                                        {deleting ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting...</> : 'Yes, Delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </div>
    );
};

export default SocialMediaManagement;
